import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';

import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { SelectOptionLoaderI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-loader-i';
import { getActiveCountryCode, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers/index';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import {
  getOpenPlatformContractData,
  getOpenPlatformContractDataRepeat,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/contracts.selectors';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { getCurrentLanguageWithLocaleInfo } from '@ordersnproducts/utils/utils';
import * as moment from 'moment/moment';
import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';
import { LoadTemperatureValues } from '../../../../actions/open-platform/added-values.actions';
import { ContractsExpirationService } from '@ordersnproducts/modules/shared/services/contracts-expiration.service';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';

@Injectable()
export class ContractsOptionLoaderOpenPlatformService implements SelectOptionLoaderI {
  constructor(
    private store: Store<OrderBuilderState>,
    private translate: TranslateService,
    private experienceService: ExperiencesService,
    private contractsExpirationService: ContractsExpirationService,
    private featureToggleService: FeatureToggleService,
  ) {}

  /**
   * Provides an observable of the contracts for single order flows
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getOpenPlatformContractData),
      filter((availableContracts) => !!availableContracts),
      mergeMap((values) =>
        this.store.select(getCurrentLanguageWithLocaleInfo).pipe(
          filter((language) => language != null),
          map((language) => {
            return { ...values, formatDate: language.formatDate };
          }),
          take(1),
        ),
      ),
      mergeMap((values) =>
        this.contractsExpirationService.maxDateTimeMoment$.pipe(
          map((maxDateTimeMoment) => ({
            ...values,
            maxDateTimeMoment,
          })),
        ),
      ),
      mergeMap((values) =>
        this.store.select(getActiveCountryCode).pipe(
          map((countryCode) => ({
            ...values,
            countryCode,
          })),
        ),
      ),
      mergeMap((values) => {
        return this.experienceService.commandDcoEnabled$().pipe(
          map((commandDcoEnabled) => {
            return {
              ...values,
              commandDcoEnabled,
            };
          }),
        );
      }),
      map(
        ({
          availableContract,
          contractbalance,
          customer,
          formatDate,
          countryCode,
          maxDateTimeMoment,
          commandDcoEnabled,
        }) => {
          formatDate = formatDate || 'MM/DD/YYYY';

          const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
          const contracts = availableContract[productCode];
          if (contracts) {
            contracts.length === 1 &&
              formGroup.get(SpecFieldNames.CONTRACT_SELECT).setValue(contracts[0].contractCode.replace(/^0+/, ''));

            const temperatureUnder = formGroup.get(SpecFieldNames.TEMPERATURE_UNDER);
            const iceQuantity = formGroup.get(SpecFieldNames.ICE_QUANTITY);
            if (
              (temperatureUnder != null && !!temperatureUnder.value) ||
              (iceQuantity != null && !!iceQuantity.value)
            ) {
              this.store.dispatch(
                new LoadTemperatureValues({
                  productCode: productCode,
                  contractCode: formGroup.get(SpecFieldNames.CONTRACT_SELECT).value,
                  formId: formGroup.get(SpecFieldNames.ID).value,
                }),
              );
            }
            return contracts.map((salesDocument) => {
              const expirationDateBeforeSLD = moment(salesDocument.validOnDateTo).isSameOrBefore(maxDateTimeMoment);
              const shouldIncludeExpirationDateUSA =
                countryCode === COUNTRY_CODE.USA && commandDcoEnabled && maxDateTimeMoment && expirationDateBeforeSLD;
              const shouldIncludeExpirationDateUK =
                countryCode === COUNTRY_CODE.UNITED_KINGDOM &&
                this.featureToggleService.isFeatureEnabled(Features.ContractsExpirationDate) &&
                maxDateTimeMoment &&
                expirationDateBeforeSLD;

              return {
                id: salesDocument.contractCode.replace(/^0+/, ''),
                name: `${this.translate.instant('views.specifications.contract')} ${salesDocument.contractCode}`,
                ...(shouldIncludeExpirationDateUSA || shouldIncludeExpirationDateUK
                  ? {
                      secondaryText: this.translate.instant(
                        'views.multiple_orders.template.contract.placeholder.expire',
                        {
                          expiration: moment(salesDocument.validOnDateTo).format(formatDate),
                        },
                      ),
                    }
                  : {}),
              };
            });
          }
          return [];
        },
      ),
    );
  }
}

/**
 * Created for new-repeat component config usage
 */
@Injectable()
export class ContractsOptionLoaderRepeatOpenPlatformService implements SelectOptionLoaderI {
  constructor(
    private store: Store<OrderBuilderState>,
    private translate: TranslateService,
  ) {}

  /**
   * Provides an observable of the products
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getOpenPlatformContractDataRepeat),
      filter((availableContracts) => !!availableContracts),
      map(({ availableContract, contractbalance, customer, recipes }) => {
        const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
        const contracts = availableContract[productCode];
        if (contracts) {
          const defaultValue = _safe(() => contracts[0].contractCode.replace(/^0+/, ''));
          const recipe = _safe(() => recipes.find((item) => item.id === formGroup.get('id').value));
          if (productCode !== recipe.values.productCode) {
            formGroup.get(SpecFieldNames.CONTRACT_SELECT).setValue(defaultValue);
          }

          return contracts.map((salesDocument) => {
            return {
              id: salesDocument.contractCode.replace(/^0+/, ''),
              name: `${this.translate.instant('views.specifications.contract')} ${salesDocument.contractCode}`,
            };
          });
        }

        return [];
      }),
    );
  }
}
